import { useEffect, useMemo, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import auditFilterIcon from 'assets/auditFilterIcon.svg';
import filledFilterIcon from 'assets/filledFilterIcon.svg';
import goodScoreIcon from 'assets/good_score_icon.svg';
import greatScoreIcon from 'assets/great_score_icon.svg';
import lowScoreIcon from 'assets/low_score_icon.svg';

import { getDesignReviewCountsAction } from 'Redux/Actions/designReviewActions';

import { PLATFORM } from 'utlis/constants';

import styles from './styles.module.scss';

export const SCORE_FILTERS = {
  LOW: 'low',
  GOOD: 'good',
  GREAT: 'great',
};

const LOW_SCORE_ICON = <img src={lowScoreIcon} width={20} height={20} alt="Low score" />;
const GOOD_SCORE_ICON = <img src={goodScoreIcon} width={20} height={20} alt="Good score" />;
const GREAT_SCORE_ICON = <img src={greatScoreIcon} width={20} height={20} alt="Great score" />;
const SELECTED_FILTER_BORDER_COLORS = {
  [SCORE_FILTERS.LOW]: '#FB9899',
  [SCORE_FILTERS.GOOD]: '#FFD230',
  [SCORE_FILTERS.GREAT]: '#7FEFA0',
};
const DEFAULT_DELTA_COLOR_CLASS = 'text-figma-gray-500';

const parseDeltaValue = value => {
  if (value == null) {
    return null;
  }

  const normalized = String(value).trim();

  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);

  return !isNaN(parsed) && isFinite(parsed) ? parsed : null;
};

const isPositiveDelta = value => {
  const parsed = parseDeltaValue(value);

  return parsed != null && parsed > 0;
};

const getDeltaPresentation = (currentCount, changeValue) => {
  const numericDiff = parseDeltaValue(changeValue);

  if (numericDiff === null) {
    return {
      mode: 'label-only',
      labelText: 'No trend data yet',
    };
  }

  if (numericDiff === 0) {
    if ((currentCount ?? 0) > 0) {
      return {
        mode: 'label-only',
        labelText: 'Same as last month',
      };
    }

    return {
      mode: 'label-only',
      labelText: 'No scores this month',
    };
  }

  return {
    mode: 'value-with-label',
    labelText: 'from last month',
  };
};

const Overview = ({ onStatusFilter, statusFilter }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  const designReviewCounts = useSelector(state => state.designReview.designReviewCounts);
  const tokenRefreshTimestamp = useSelector(state => state.userStore.tokenRefreshTimestamp);

  const fetchDesignReviewCounts = async () => {
    setIsLoading(true);

    try {
      await dispatch(getDesignReviewCountsAction(PLATFORM));
    }
    finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDesignReviewCounts();
  }, [dispatch]);

  useEffect(() => {
    if (tokenRefreshTimestamp) {
      fetchDesignReviewCounts();
    }
  }, [tokenRefreshTimestamp]);

  const globalStats = useMemo(() => [
    { label: 'Reviews this month', value: designReviewCounts?.reviews_this_month ?? 0 },
    { label: 'Total screens analyzed', value: designReviewCounts?.total_screens_analyzed ?? 0 },
  ], [designReviewCounts]);

  const npsStats = useMemo(() => [
    {
      value: designReviewCounts?.low_score_count ?? 0,
      label: 'Low scores',
      changeValue: designReviewCounts?.low_score_difference ?? '0',
      changeLabel: 'from last month',
      positiveDeltaColorClass: 'text-[#F75A5F]',
      colorOnPositiveOnly: true,
      icon: LOW_SCORE_ICON,
      showFilter: true,
      scoreFilter: SCORE_FILTERS.LOW,
      tooltipText: 'Flows with NPS below 0 that need immediate attention and optimization',
    },
    {
      value: designReviewCounts?.good_score_count ?? 0,
      label: 'Good scores',
      changeValue: designReviewCounts?.good_score_difference ?? '0',
      changeLabel: 'from last month',
      positiveDeltaColorClass: DEFAULT_DELTA_COLOR_CLASS,
      colorOnPositiveOnly: false,
      icon: GOOD_SCORE_ICON,
      showFilter: true,
      scoreFilter: SCORE_FILTERS.GOOD,
      tooltipText: 'Flows scoring 0-39 with potential for improvement through strategic changes',
    },
    {
      value: designReviewCounts?.great_score_count ?? 0,
      label: 'Great scores',
      changeValue: designReviewCounts?.great_score_difference ?? '0',
      changeLabel: 'from last month',
      positiveDeltaColorClass: 'text-[#22C14D]',
      colorOnPositiveOnly: true,
      icon: GREAT_SCORE_ICON,
      showFilter: true,
      scoreFilter: SCORE_FILTERS.GREAT,
      tooltipText: 'Top performing flows scoring above 40 with minor improvements needed',
    },
  ], [designReviewCounts]);

  return (
    <div className="mb-10">
      <div className="mb-4">
        <h2 className="font-inter text-base font-semibold text-figma-gray-900 leading-6">
          Overview
        </h2>
      </div>

      {isLoading
        ? (
          <div className="flex justify-center items-center h-[100px]">
            <ImpulseSpinner frontColor={'#28BDAE'} />
          </div>
        )
        : (
          <div className="flex items-stretch gap-12">
            {/* Global card — two stacked rows, each 50px */}
            <div
              className="flex flex-col w-[300px] shrink-0 h-[100px] bg-white
                border border-figma-gray-200 rounded-lg shadow-figma-sm"
            >
              {globalStats.map((stat, i) => (
                <div
                  key={i}
                  className={`flex flex-1 flex-row justify-between items-center px-16
                    ${i === 0 ? 'border-b border-[#F1F2F5]' : ''}`}
                >
                  <span className="font-inter text-sm font-normal text-figma-gray-900">
                    {stat.label}
                  </span>
                  <span className="font-inter text-sm font-semibold text-figma-gray-900">
                    {stat.value}
                  </span>
                </div>
              ))}
            </div>

            {/* NPS stat cards */}
            {npsStats.map((stat, i) => {
              const deltaPresentation = getDeltaPresentation(stat.value, stat.changeValue);

              return (
                <div
                  key={i}
                  className={cn(
                    'flex flex-col flex-1 justify-between h-[100px] bg-white border border-figma-gray-200'
                    + ' rounded-lg shadow-figma-sm px-16 py-12',
                    styles['metrics-card']
                  )}
                  style={
                    statusFilter === stat.scoreFilter
                      ? { borderColor: SELECTED_FILTER_BORDER_COLORS[stat.scoreFilter] }
                      : undefined
                  }
                >
                  {/* Header: metric+label on the left, filter on the right */}
                  <div className="flex flex-row items-start justify-between">
                    <div className="flex flex-col gap-0">
                      <div className="flex flex-row items-center gap-8">
                        {stat.icon}
                        <span className="font-inter text-2xl font-semibold text-figma-gray-900 leading-8">
                          {stat.value}
                        </span>
                      </div>
                      <span className="font-inter text-sm font-normal text-figma-gray-500 leading-5">
                        {stat.label}
                      </span>
                    </div>
                    {stat.showFilter && (
                      <button
                        className="bg-transparent border-none cursor-pointer p-0 shrink-0"
                        onClick={() => onStatusFilter(
                          statusFilter === stat.scoreFilter ? null : stat.scoreFilter
                        )}
                        title={`Filter ${stat.label.toLowerCase()}`}
                      >
                        <img
                          src={statusFilter === stat.scoreFilter ? filledFilterIcon : auditFilterIcon}
                          alt="Filter"
                          width={20}
                          height={20}
                        />
                      </button>
                    )}
                  </div>

                  {/* Change row */}
                  <div className="flex flex-row items-center gap-4">
                    {deltaPresentation.mode === 'value-with-label'
                      ? (
                        <>
                          <span
                            className={
                              `font-inter text-xs font-semibold leading-4 ${
                                stat.colorOnPositiveOnly
                                  ? (isPositiveDelta(stat.changeValue)
                                    ? stat.positiveDeltaColorClass
                                    : DEFAULT_DELTA_COLOR_CLASS)
                                  : DEFAULT_DELTA_COLOR_CLASS
                              }`
                            }
                          >
                            {stat.changeValue}
                          </span>
                          <span className="font-inter text-xs font-normal text-figma-gray-500 leading-4">
                            {deltaPresentation.labelText}
                          </span>
                        </>
                      )
                      : (
                        <span className="font-inter text-xs font-normal text-figma-gray-500 leading-4">
                          {deltaPresentation.labelText}
                        </span>
                      )}
                  </div>
                  <div className={styles['metric-tooltip']}>
                    {stat.tooltipText}
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
};

Overview.propTypes = {
  onStatusFilter: PropTypes.func.isRequired,
  statusFilter: PropTypes.string,
};

export default Overview;
